/**
 * @author      Antigravity AI
 * @date        June, 2026
 * @copyright   www.ibirdsservices.com
 */

import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
  Spinner,
  Alert,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import WhatsAppAPI from "../../api/WhatsAppAPI";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaSave, FaExchangeAlt } from "react-icons/fa";

const FacebookFormMapping = () => {
  const navigate = useNavigate();

  // State Variables
  const [settings, setSettings] = useState([]);
  const [selectedSettingId, setSelectedSettingId] = useState("");
  const [selectedSetting, setSelectedSetting] = useState(null);

  const [forms, setForms] = useState([]);
  const [selectedFormId, setSelectedFormId] = useState("");
  const [selectedForm, setSelectedForm] = useState(null);

  const [questions, setQuestions] = useState([]);
  const [leadFields, setLeadFields] = useState([]);
  const [mappings, setMappings] = useState({}); // lead_field_key -> facebook_field_key
  const [activeLeadFields, setActiveLeadFields] = useState(["firstname", "lastname", "whatsapp_number"]);

  // Loading & Error States
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [loadingForms, setLoadingForms] = useState(false);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchLeadFields();
  }, []);

  // Fetch all Instagram settings (Step 1)
  const fetchSettings = async () => {
    try {
      setLoadingSettings(true);
      const response = await WhatsAppAPI.getInstagramSettingRecord();
      if (response.success) {
        setSettings(response.record || []);
      } else {
        toast.error("Failed to load Instagram settings.");
      }
    } catch (error) {
      console.error("Error fetching Instagram settings:", error);
      toast.error("Error loading Instagram settings.");
    } finally {
      setLoadingSettings(false);
    }
  };

  // Fetch lead fields (Step 4)
  const fetchLeadFields = async () => {
    try {
      const response = await WhatsAppAPI.getLeadFieldsForMapping();
      if (response.success) {
        setLeadFields(response.fields || []);
      }
    } catch (error) {
      console.error("Error fetching lead fields:", error);
    }
  };

  // Handle Setting selection (Step 1)
  const handleSettingChange = async (e) => {
    const pageId = e.target.value;
    setSelectedSettingId(pageId);
    setSelectedFormId("");
    setForms([]);
    setQuestions([]);
    setMappings({});
    setActiveLeadFields(["firstname", "lastname", "whatsapp_number"]);

    if (!pageId) {
      setSelectedSetting(null);
      return;
    }

    const setting = settings.find((s) => s.page_id === pageId);
    setSelectedSetting(setting);

    // Load active forms for this setting (Step 2)
    try {
      setLoadingForms(true);
      const response = await WhatsAppAPI.getFacebookForms(setting.page_id);
      if (response.success) {
        setForms(response.data || []);
        if (response.data?.length === 0) {
          toast.info("No Leadgen forms found on this Facebook page.");
        }
      } else {
        toast.error("Failed to fetch forms from Facebook.");
      }
    } catch (error) {
      console.error("Error loading Facebook forms:", error);
      toast.error("Error loading Facebook forms.");
    } finally {
      setLoadingForms(false);
    }
  };

  // Handle Form selection (Step 3 & 8)
  const handleFormChange = async (e) => {
    const formId = e.target.value;
    setSelectedFormId(formId);
    setQuestions([]);
    setMappings({});
    setActiveLeadFields(["firstname", "lastname", "whatsapp_number"]);

    if (!formId) {
      setSelectedForm(null);
      return;
    }

    const form = forms.find((f) => f.id === formId);
    setSelectedForm(form);

    try {
      setLoadingQuestions(true);

      // Load form questions from Facebook (Step 3)
      const questionsPromise = WhatsAppAPI.getFacebookFormQuestions(selectedSetting.page_id, formId);
      // Check existing mapping in database (Step 8)
      const mappingPromise = WhatsAppAPI.getFacebookFormMapping(formId);

      const [questionsRes, mappingRes] = await Promise.all([questionsPromise, mappingPromise]);

      if (questionsRes.success) {
        setQuestions(questionsRes.questions || []);

        // Initialise mappings with empty values
        const initialMappings = {
          firstname: "",
          lastname: "",
          whatsapp_number: ""
        };
        const initialActiveFields = ["firstname", "lastname", "whatsapp_number"];

        // Prepopulate with existing mapping if it exists in DB (Step 8)
        if (mappingRes.success && mappingRes.mapping) {
          const dbMapping = mappingRes.mapping.mapping || {}; // fb_key -> lead_key
          Object.entries(dbMapping).forEach(([fbKey, leadKey]) => {
            if (leadKey) {
              initialMappings[leadKey] = fbKey;
              if (!initialActiveFields.includes(leadKey)) {
                initialActiveFields.push(leadKey);
              }
            }
          });
          toast.success("Loaded existing form mapping.");
        }

        setMappings(initialMappings);
        setActiveLeadFields(initialActiveFields);
      } else {
        toast.error("Failed to fetch form questions from Facebook.");
      }
    } catch (error) {
      console.error("Error loading form details:", error);
      toast.error("Error loading form details.");
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Handle individual mapping dropdown change
  const handleMappingChange = (leadFieldKey, fbKey) => {
    setMappings((prev) => ({
      ...prev,
      [leadFieldKey]: fbKey,
    }));
  };

  // Add a lead field row
  const handleAddFieldRow = (fieldKey) => {
    if (!fieldKey) return;
    if (!activeLeadFields.includes(fieldKey)) {
      setActiveLeadFields((prev) => [...prev, fieldKey]);
      setMappings((prev) => ({
        ...prev,
        [fieldKey]: ""
      }));
    }
  };

  // Remove a lead field row
  const handleRemoveFieldRow = (fieldKey) => {
    setActiveLeadFields((prev) => prev.filter((f) => f !== fieldKey));
    setMappings((prev) => {
      const updated = { ...prev };
      delete updated[fieldKey];
      return updated;
    });
  };

  // Save Mapping (Step 6 & 7)
  const handleSaveMapping = async () => {
    if (!selectedSettingId || !selectedFormId) {
      toast.warning("Please select setting and form first.");
      return;
    }

    // Filter out fields with empty mappings, and invert to fb_key -> lead_key for DB
    const finalMapping = {};
    let mappedCount = 0;
    Object.keys(mappings).forEach((leadKey) => {
      const fbKey = mappings[leadKey];
      if (fbKey) {
        finalMapping[fbKey] = leadKey;
        mappedCount++;
      }
    });

    if (mappedCount === 0) {
      toast.warning("Please map at least one field.");
      return;
    }

    try {
      setSavingMapping(true);
      const reqBody = {
        instagram_setting_id: selectedSetting.id,
        page_id: selectedSetting.page_id,
        form_id: selectedFormId,
        form_name: selectedForm.name,
        mapping: finalMapping,
      };

      const response = await WhatsAppAPI.saveFacebookFormMapping(reqBody);
      if (response.success) {
        toast.success("Form mapping saved successfully.");
      } else {
        toast.error(response.errors || "Failed to save mapping.");
      }
    } catch (error) {
      console.error("Error saving mapping:", error);
      toast.error("Error occurred while saving mapping.");
    } finally {
      setSavingMapping(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="mx-2 mb-4 align-items-center">
        <Col xs={12} md={8} className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            className="btn-sm d-flex align-items-center gap-1 border-0"
            onClick={() => navigate("/instagram_setting")}
          >
            <FaArrowLeft /> Back
          </Button>
          <h4 className="fw-bold mb-0">📋 Facebook Instant Form Mapping</h4>
        </Col>
      </Row>

      <Row className="mx-2 mb-4">
        <Card className="p-4 header-bg shadow-sm border-0 rounded-3">
          <h5 className="mb-2 fw-bold">💡 Quick Guide</h5>
          <small>
            Select a Facebook page integration, choose an active Lead Form, and map each questionnaire question (left) to the corresponding field in your CRM Lead records (right).
          </small>
        </Card>
      </Row>

      <Row className="mx-2 g-4">
        {/* Sidebar Configuration */}
        <Col lg={4}>
          <Card className="shadow-sm border-0 rounded-3 p-4 bg-white">
            <h5 className="fw-semibold mb-4 text-dark border-bottom pb-2">⚙️ Configurations</h5>

            {/* Select Integration */}
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold text-secondary small">Step 1: Select Integration</Form.Label>
              {loadingSettings ? (
                <div className="d-flex align-items-center gap-2 text-muted py-2">
                  <Spinner animation="border" size="sm" />
                  <span className="small">Loading integrations...</span>
                </div>
              ) : (
                <Form.Select value={selectedSettingId} onChange={handleSettingChange}>
                  <option value="">-- Choose Integration --</option>
                  {settings.map((s) => (
                    <option key={s.id} value={s.page_id}>
                      {s.name || `Page ID: ${s.page_id}`}
                    </option>
                  ))}
                </Form.Select>
              )}
            </Form.Group>

            {/* Select Lead Form */}
            {selectedSettingId && (
              <Form.Group className="mb-3">
                <Form.Label className="fw-semibold text-secondary small">Step 2: Select Lead Form</Form.Label>
                {loadingForms ? (
                  <div className="d-flex align-items-center gap-2 text-muted py-2">
                    <Spinner animation="border" size="sm" />
                    <span className="small">Loading forms from Meta...</span>
                  </div>
                ) : (
                  <Form.Select value={selectedFormId} onChange={handleFormChange}>
                    <option value="">-- Choose Form --</option>
                    {forms.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            )}

            {/* Display metadata */}
            {selectedForm && (
              <Alert variant="info" className="mt-4 border-0 rounded-3 py-3 px-3 shadow-none">
                <div className="d-flex flex-column gap-2 small">
                  <div>
                    <span className="fw-bold">Page ID:</span>{" "}
                    <code className="text-dark bg-light px-1 py-0.5 rounded">{selectedSetting?.page_id}</code>
                  </div>
                  <div>
                    <span className="fw-bold">Form ID:</span>{" "}
                    <code className="text-dark bg-light px-1 py-0.5 rounded">{selectedFormId}</code>
                  </div>
                  <div>
                    <span className="fw-bold">Status:</span>{" "}
                    <span className="badge bg-success">Active</span>
                  </div>
                </div>
              </Alert>
            )}
          </Card>
        </Col>

        {/* Question Mappings Container */}
        <Col lg={8}>
          <Card className="shadow-sm border-0 rounded-3 p-4 bg-white" style={{ minHeight: "350px" }}>
            <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
              <h5 className="fw-semibold text-dark mb-0">🔗 Question Mappings</h5>
              {selectedFormId && (
                <Button
                  variant="primary"
                  className="btn-sm px-3 d-flex align-items-center gap-2 shadow-sm"
                  onClick={handleSaveMapping}
                  disabled={savingMapping}
                >
                  {savingMapping ? (
                    <>
                      <Spinner animation="border" size="sm" /> Saving...
                    </>
                  ) : (
                    <>
                      <FaSave /> Save Mapping
                    </>
                  )}
                </Button>
              )}
            </div>

            {!selectedSettingId ? (
              <div className="text-center py-5 my-3 text-muted">
                <FaExchangeAlt size={45} className="mb-3 text-secondary opacity-50" />
                <p className="mb-1 fw-semibold">No Integration Selected</p>
                <small className="text-muted d-block">
                  Please select an Instagram/Facebook setting integration from the left configuration panel to begin.
                </small>
              </div>
            ) : !selectedFormId ? (
              <div className="text-center py-5 my-3 text-muted">
                <FaExchangeAlt size={45} className="mb-3 text-secondary opacity-50" />
                <p className="mb-1 fw-semibold">No Lead Form Selected</p>
                <small className="text-muted d-block">
                  Please select an active Facebook Lead Form from the left configuration panel to load questions.
                </small>
              </div>
            ) : loadingQuestions ? (
              <div className="text-center py-5 my-3 text-muted">
                <Spinner animation="border" className="mb-3 text-primary" />
                <p className="small mb-0">Loading form questions and existing mappings from Meta Graph...</p>
              </div>
            ) : questions.length === 0 ? (
              <Alert variant="warning" className="border-0 rounded-3">
                No questions found inside the selected form. Make sure the form is correctly setup on Facebook.
              </Alert>
            ) : (
              <div className="table-responsive">
                <Table borderless className="align-middle table-hover mb-0">
                  <thead className="bg-light">
                    <tr style={{ fontSize: "0.85rem", textTransform: "uppercase", color: "#6c757d" }}>
                      <th className="py-3 px-3">CRM Lead Field</th>
                      <th className="py-3 px-3">Facebook Form Question</th>
                      <th className="py-3 px-3" style={{ width: "100px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeLeadFields.map((leadKey) => {
                      const fieldInfo = leadFields.find((lf) => lf.key === leadKey) || { key: leadKey, label: leadKey };
                      const isRequired = ["firstname", "lastname", "whatsapp_number"].includes(leadKey);
                      
                      return (
                        <tr key={leadKey} className="border-bottom">
                          <td className="py-3 px-3">
                            <div className="fw-semibold text-dark">
                              {fieldInfo.label} {isRequired && <span className="text-danger">*</span>}
                            </div>
                            <code className="text-muted small" style={{ fontSize: "0.75rem" }}>
                              field: {leadKey}
                            </code>
                          </td>
                          <td className="py-3 px-3">
                            <Form.Select
                              value={mappings[leadKey] || ""}
                              onChange={(e) => handleMappingChange(leadKey, e.target.value)}
                              style={{ maxWidth: "350px" }}
                            >
                              <option value="">-- Select Facebook Question --</option>
                              {questions.map((q) => (
                                <option key={q.key} value={q.key}>
                                  {q.label || q.key} ({q.key})
                                </option>
                              ))}
                            </Form.Select>
                          </td>
                          <td className="py-3 px-3">
                            {!isRequired && (
                              <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleRemoveFieldRow(leadKey)}
                                className="border-0 py-1"
                              >
                                Remove
                              </Button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                {leadFields.filter((lf) => !activeLeadFields.includes(lf.key)).length > 0 && (
                  <div className="d-flex align-items-center gap-2 mt-4 pt-3 border-top">
                    <Form.Select
                      id="addFieldSelect"
                      className="form-select-sm"
                      style={{ maxWidth: "250px" }}
                      defaultValue=""
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val) {
                          handleAddFieldRow(val);
                          e.target.value = "";
                        }
                      }}
                    >
                      <option value="">-- Add Mapped CRM Lead Field --</option>
                      {leadFields
                        .filter((lf) => !activeLeadFields.includes(lf.key))
                        .map((lf) => (
                          <option key={lf.key} value={lf.key}>
                            {lf.label}
                          </option>
                        ))}
                    </Form.Select>
                  </div>
                )}
              </div>
            )}
          </Card>
        </Col>
      </Row>
      <ToastContainer autoClose={3000} />
    </Container>
  );
};

export default FacebookFormMapping;
